// import { faPlus } from '@fortawesome/free-solid-svg-icons';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { FC, FormEvent, ReactNode } from 'react';
import {
  FloatingLabel,
  Form,
  Modal,
  ToastContainer,
  Spinner
} from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { useCalendarContext, CalendarEvent } from 'providers/CalendarProvider';
import {
  ADD_NEW_EVENT,
  CLOSE_MODAL_EVENT,
  SET_CALENDAR_STATE
} from 'reducers/CalendarReducer';
import usePhoenixForm from 'hooks/usePhoenixForm';
import axios from 'axios';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import Toast from 'react-bootstrap/Toast';
import { UilCalendarAlt, UilTimes } from '@iconscout/react-unicons';

import { useCarManagmentContext } from 'providers/CarManagmentProvider';
import { useAppContext } from 'providers/AppProvider';

require('dayjs/locale/pt');

dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(LocalizedFormat);
dayjs.extend(weekday);
dayjs.locale('pt');

type Company = {
  idlocal: number;
  nome_curto: string;
  nome_longo: string;
};

type User = {
  id_user: number;
  name: string;
};

type SelectData = {
  label: string;
  value: number;
};

const normalizeSelectText = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const getEditDistance = (firstText: string, secondText: string) => {
  const previousRow = Array.from(
    { length: secondText.length + 1 },
    (_, index) => index
  );

  for (let firstIndex = 1; firstIndex <= firstText.length; firstIndex++) {
    const currentRow = [firstIndex];

    for (let secondIndex = 1; secondIndex <= secondText.length; secondIndex++) {
      currentRow[secondIndex] = Math.min(
        currentRow[secondIndex - 1] + 1,
        previousRow[secondIndex] + 1,
        previousRow[secondIndex - 1] +
          (firstText[firstIndex - 1] === secondText[secondIndex - 1] ? 0 : 1)
      );
    }

    previousRow.splice(0, previousRow.length, ...currentRow);
  }

  return previousRow[secondText.length];
};

const matchesSelectOption = (label: string, input: string) => {
  const normalizedLabel = normalizeSelectText(label);
  const normalizedInput = normalizeSelectText(input);

  if (!normalizedInput || normalizedLabel.includes(normalizedInput)) {
    return true;
  }

  const allowedErrors = normalizedInput.length >= 5 ? 1 : 0;
  for (
    let startIndex = 0;
    startIndex <= normalizedLabel.length - normalizedInput.length;
    startIndex++
  ) {
    const labelPart = normalizedLabel.slice(
      startIndex,
      startIndex + normalizedInput.length
    );

    if (getEditDistance(normalizedInput, labelPart) <= allowedErrors) {
      return true;
    }
  }

  return false;
};

type Location = {
  short: string;
  long: string;
  road: string;
  postalCode: string;
  city: string;
  nif?: string;
};

type TimePreset = 'morning' | 'afternoon' | 'allday' | 'custom';

type UserConflict = {
  id: number;
  start: Date;
  end: Date;
  name: string;
};

type GroupedOverlapData = {
  [user: string]: string[];
};

interface OverlapListProps {
  groupedData: GroupedOverlapData;
}

const apiClient = axios.create({
  baseURL: 'https://wsdigital.ctcp.pt/gcViagems',
  // baseURL: 'http://localhost:3400/gcViagems',
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const CalendarAddNewEventModal = () => {
  const {
    openNewEventModal,
    newEventModalMode,
    calendarDispatch,
    updatingTrip
  } = useCalendarContext();
  const { getCurrentUser } = useAppContext();
  const { refreshTrips } = useCarManagmentContext();
  const currentUser = getCurrentUser();

  const [selectDataCompanies, setSelectDataCompanies] = useState<SelectData[]>(
    []
  );
  const [selectDataUsers, setSelectDataUsers] = useState<SelectData[]>([]);

  const [startValidity, setStartValidity] = useState(true);
  const [endValidity, setEndValidity] = useState(true);
  const [pastDate, setPastDate] = useState(true);
  const [locationValidity, setLocationValidity] = useState(true);
  const [passengerValidity, setPassengerValidity] = useState(true);

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [timePreset, setTimePreset] = useState<TimePreset>('allday');
  const [customTimes, setCustomTimes] = useState({
    start: '09:00',
    end: '18:00'
  });

  const [preSelectedCompanies, setPreSelectedCompanies] = useState<
    SelectData[]
  >([]);
  const [preSelectedUsers, setPreSelectedUsers] = useState<SelectData[]>([]);
  const [tempOutsidePassengersAmount, setTempOutsidePassengersAmount] =
    useState<string>('0');

  // usado para criar localizações
  const [createLocation, setCreateLocation] = useState(false);
  const [locationShortValidity, setLocationShortValidity] = useState(true);
  const [locationLongValidity, setLocationLongValidity] = useState(true);
  const [locationRoadValidity, setLocationRoadValidity] = useState(true);
  const [locationPostalCodeValidity, setLocationPostalCodeValidity] =
    useState(true);
  const [locationCityValidity, setLocationCityValidity] = useState(true);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    'Verifique a sua connexão ou tente mais tarde.'
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingRequest, setIsCreatingRequest] = useState(false);
  const [isSavingRequest, setIsSavingRequest] = useState(false);
  const [isDeletingSingleRequest, setIsDeletingSingleRequest] = useState(false);
  const [isDeletingAllRequests, setIsDeletingAllRequests] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastDetails, setToastDetails] = useState<string | ReactNode>('');

  const getListOfCompanies = async () => {
    try {
      const response = await apiClient.get('/companiesList');

      const companies = response.data.map((company: Company) => ({
        value: company.idlocal,
        label: `${company.nome_curto} ${company.nome_longo ? `- ${company.nome_longo}` : ``}`
      }));

      setSelectDataCompanies(companies);
    } catch (error) {
      /* empty */
    }
  };

  // get entity's users list
  const getListOfUsers = async () => {
    try {
      const response = await apiClient.get('/entitiesUsers');
      const users = response.data.map((user: User) => ({
        value: user.id_user,
        label: user.name
      }));
      setSelectDataUsers(users);
    } catch (error) {
      /* empty */
    }
  };

  const {
    formData: requestData,
    setFormData: setFormRequestData,
    // onChange: onRequestChange,
    reset: resetRequest
  } = usePhoenixForm<CalendarEvent>({
    start: `${dayjs(new Date()).format('YYYY-MM-DD')} 09:00:00`,
    end: `${dayjs(new Date()).format('YYYY-MM-DD')} 18:00:00`,
    location: [],
    users: [],
    outsideUsers: 0,
    takecar: 'N',
    leavecar: 'N',
    canChangeTime: 'Y'
  });

  const {
    formData: locationData,
    setFormData: setFormLocationData,
    // onChange: onLocationChange,
    reset: resetLocation
  } = usePhoenixForm<Location>({
    short: '',
    long: '',
    road: '',
    postalCode: '',
    city: '',
    nif: ''
  });

  const [locationNifValidity, setLocationNifValidity] = useState(true);

  const reset = () => {
    resetRequest();
    resetLocation();
    setShowToast(false);
    setStartValidity(true);
    setEndValidity(true);
    setPastDate(true);
    setLocationValidity(true);
    setPassengerValidity(true);
    setPreSelectedCompanies([]);
    setPreSelectedUsers([]);
    setTempOutsidePassengersAmount('0');
    setCustomTimes({ start: '09:00', end: '18:00' });
    setTimePreset('allday');
    setSelectedDay(null);
    setLocationNifValidity(true);
  };

  // Helper to sync the preset/day to the main requestData
  const updateEventTimes = (
    day: Date | null,
    preset: TimePreset,
    custom?: { start: string; end: string }
  ) => {
    if (!day) return;

    const dateNow = dayjs();
    const roundedDate = dateNow
      .add(5 - (dateNow.minute() % 5), 'minute')
      .second(0) // zero out seconds
      .format('HH:mm:ss');

    const dateStr =
      newEventModalMode == 'edit'
        ? dayjs(updatingTrip?.originalStart).format('YYYY-MM-DD')
        : dayjs(day).format('YYYY-MM-DD');

    let startTime = '';
    let endTime = '';

    switch (preset) {
      case 'morning':
        startTime = '09:00:00';
        endTime = '12:30:00';
        break;
      case 'afternoon':
        startTime = '14:00:00';
        endTime = '18:00:00';
        break;
      case 'allday':
        startTime = '09:00:00';
        endTime = '18:00:00';
        break;
      case 'custom':
        startTime = `${custom?.start || '09:00'}:00`;
        endTime = `${custom?.end || '18:00'}:00`;
        break;
    }

    if (
      dateStr == dateNow.format('YYYY-MM-DD') &&
      startTime < roundedDate &&
      roundedDate < endTime &&
      newEventModalMode == 'create'
    ) {
      // change time so you can mark for today
      startTime = roundedDate;
    }

    const nextRequestData = {
      ...requestData,
      start: `${dateStr} ${startTime}`,
      end: `${dateStr} ${endTime}`
    };

    setFormRequestData(nextRequestData);

    return nextRequestData;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newEventModalMode === 'create') {
      setIsCreatingRequest(true);
    } else {
      setIsSavingRequest(true);
    }

    const setSubmitLoading = (loading: boolean) => {
      if (newEventModalMode === 'create') {
        setIsCreatingRequest(loading);
      } else {
        setIsSavingRequest(loading);
      }
    };

    let isValid = true;
    const errors: string[] = [];

    const currentRequestData = requestData; // || nextRequestData

    if (!selectedDay) {
      setPastDate(false);
      errors.push('É necessário selecionar uma data');
      isValid = false;
    }

    // verify data availabitliy
    if (!currentRequestData) {
      setStartValidity(false);
      setEndValidity(false);
      setPastDate(false);
      setLocationValidity(false);

      setSubmitLoading(false);
      return;
    }

    if (newEventModalMode == 'create') {
      // sem data de incio ou não é valida (formato)
      if (
        !dayjs(currentRequestData.start, 'YYYY-MM-DD HH:mm:ss', true).isValid()
      ) {
        setStartValidity(false);
        errors.push('Sem data de início válida');
        isValid = false;
      }

      // sem data de fim ou não é valida (formato)
      if (
        !dayjs(currentRequestData.end, 'YYYY-MM-DD HH:mm:ss', true).isValid()
      ) {
        setEndValidity(false);
        errors.push('Sem data de fim válida');
        isValid = false;
      }

      if (
        dayjs(currentRequestData.start, 'YYYY-MM-DD HH:mm:ss', true).isAfter(
          dayjs(currentRequestData.end, 'YYYY-MM-DD HH:mm:ss', true)
        )
      ) {
        setStartValidity(false);
        setEndValidity(false);
        errors.push('Data de inicio tem de ser antes da data de fim.');
        isValid = false;
      }

      // data de inicio antes de agora
      if (
        !dayjs(currentRequestData.end, 'YYYY-MM-DD HH:mm:ss', true).isAfter(
          new Date()
        )
      ) {
        setStartValidity(false);
        setEndValidity(false);
        setPastDate(false);
        errors.push('Horário inválido (já passou)');
        isValid = false;
      }

      // sem destinos
      if (
        !currentRequestData.location ||
        currentRequestData.location.length < 1
      ) {
        setLocationValidity(false);
        errors.push('Sem destinos de visita');
        isValid = false;
      }

      if (!isValid) {
        // compile error message
        setSubmitLoading(false);
        return;
      }

      // create request
      try {
        const payload = {
          ...currentRequestData,
          id: updatingTrip?.grupo
        };
        await apiClient.post('/pedido', payload);
      } catch (error) {
        setSubmitLoading(false);
        return;
      }

      // creates an event on the calendar (not being used now)
      const selectedLocationsIds = currentRequestData.location;
      const selectedLocations = selectDataCompanies.filter(company =>
        selectedLocationsIds?.includes(company.value)
      );
      const eventTitle = selectedLocations.map(l => l.label).join(', ');
      const eventPayload: CalendarEvent = {
        ...currentRequestData,
        title: eventTitle,
        className: 'text-primary',
        start: currentRequestData.start,
        end: currentRequestData.end,
        outsideUsers: currentRequestData.outsideUsers || 0,
        location: undefined
      };

      calendarDispatch({
        type: ADD_NEW_EVENT,
        payload: eventPayload
      });

      reset();
      refreshTrips();
      setSubmitLoading(false);
    } else if (newEventModalMode == 'edit') {
      if (
        dayjs(currentRequestData.start).isBefore(dayjs()) &&
        dayjs(currentRequestData.start) == dayjs(updatingTrip?.originalStart)
      ) {
        setStartValidity(false);
        errors.push('A data de inicio não pode ser anterior a atual.');
        isValid = false;
      }

      if (
        dayjs(currentRequestData.end).isBefore(dayjs(currentRequestData.start))
      ) {
        setEndValidity(false);
        errors.push('A data de fim não pode ser anterior a de início.');
        isValid = false;
      }

      if (dayjs(currentRequestData.end).isBefore(dayjs())) {
        setEndValidity(false);
        errors.push('A data de fim não pode ser anterior a hora atual.');
        isValid = false;
      }

      // sem destinos
      if (
        !currentRequestData.location ||
        currentRequestData.location.length < 1
      ) {
        setLocationValidity(false);
        errors.push('Sem destinos de visita');
        isValid = false;
      }

      const hasPassengers =
        (currentRequestData.users?.length ?? 0) > 0 ||
        (currentRequestData.outsideUsers ?? 0) > 0;

      if (!hasPassengers) {
        setPassengerValidity(false);
        errors.push(
          'É necessário selecionar pelo menos um passageiro ou indicar passageiros externos.'
        );
        isValid = false;
      }

      if (!isValid) {
        setSubmitLoading(false);
        return;
      }

      // create request
      try {
        const payload = {
          ...currentRequestData,
          id: updatingTrip?.grupo
        };

        await apiClient.post('/updateUserRequest', payload);
      } catch (error) {
        setSubmitLoading(false);
        if (axios.isAxiosError(error)) {
          setErrorMessage(error.response?.data.message);
        } else {
          setErrorMessage(`Erro imprevisto: ${error}`);
        }
        setOpenAlertModal(true);
        return;
      }

      calendarDispatch({
        type: CLOSE_MODAL_EVENT
      });

      reset();
      refreshTrips();
      setSubmitLoading(false);
    }
  };

  const handleClose = () => {
    calendarDispatch({
      type: SET_CALENDAR_STATE,
      payload: {
        openNewEventModal: false,
        newEventModalMode: 'create'
      }
    });

    reset();
  };

  const deleteRequest = async (targetedRequest: 'single' | 'all') => {
    // setDeleteModalOpen(true);
    const setDeleteLoading = (loading: boolean) => {
      if (targetedRequest === 'single') {
        setIsDeletingSingleRequest(loading);
      } else {
        setIsDeletingAllRequests(loading);
      }
    };
    setDeleteLoading(true);
    calendarDispatch({
      type: CLOSE_MODAL_EVENT
    });

    // if (updatingTrip?.id == updatingTrip?.grupo) {
    const payload = {
      id: updatingTrip?.id,
      grupo: updatingTrip?.grupo,
      target: targetedRequest,
      state: 'Cancelado por CTCP'
    };

    try {
      await apiClient.post('/deleteRequests', payload);
    } catch (error) {
      setDeleteLoading(false);
      if (axios.isAxiosError(error)) {
        setErrorMessage(error.response?.data.message);
      } else {
        setErrorMessage(`Erro imprevisto: ${error}`);
      }
      setOpenAlertModal(true);
    }

    reset();
    refreshTrips();
    setDeleteLoading(false);
    setDeleteModalOpen(false);
  };

  const handleLocationCreation = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    let isValid = true;
    const errors = [];

    if (locationData.short === '') {
      setLocationShortValidity(false);
      errors.push('No short name');
      isValid = false;
    }

    if (locationData.long === '') {
      setLocationLongValidity(false);
      errors.push('No long name');
      isValid = false;
    }

    if (locationData.road === '') {
      setLocationRoadValidity(false);
      errors.push('No road');
      isValid = false;
    }

    const postal = (locationData.postalCode || '').trim();
    if (!/^[0-9]{4}-[0-9]{3}$/.test(postal)) {
      setLocationPostalCodeValidity(false);
      errors.push('Código postal inválido (ex: 1234-567)');
      isValid = false;
    }

    // NIF validation: digits only, max 14 digits
    const nifValue = (locationData.nif || '').trim();
    if (nifValue === '' || !/^[0-9]{1,14}$/.test(nifValue)) {
      setLocationNifValidity(false);
      errors.push('No or invalid NIF');
      isValid = false;
    }

    if (locationData.city === '') {
      setLocationCityValidity(false);
      errors.push('No city');
      isValid = false;
    }

    if (!isValid) {
      setIsLoading(false);
      return;
    } else {
      try {
        await apiClient.post('/createLocation', locationData);
      } catch (error) {
        setIsLoading(false);
        if (axios.isAxiosError(error)) {
          setErrorMessage(error.response?.data.message);
        } else {
          setErrorMessage(`Erro imprevisto: ${error}`);
        }
        setOpenAlertModal(true);
      }

      setIsLoading(false);
      resetLocation();
      getListOfCompanies();
      setCreateLocation(false);
    }
  };

  const stopLocationCreation = () => {
    setCreateLocation(false);
  };

  useEffect(() => {
    if (updatingTrip && openNewEventModal && newEventModalMode == 'edit') {
      const actualHours = {
        start: dayjs(updatingTrip.originalStart).format('HH:mm'),
        end: dayjs(updatingTrip.originalEnd).format('HH:mm')
      };

      if (updatingTrip.companiesList) {
        const selectedValueObjects = updatingTrip.companiesList
          ?.split(', ')
          .map(n => Number(n))
          .map(id => {
            const found = selectDataCompanies.find(item => item.value === id);

            return found ? found : { value: id, label: `ID: ${id}` };
          });

        setPreSelectedCompanies(selectedValueObjects);
      }

      if (updatingTrip.listPassenger) {
        const selectedValueObjects = updatingTrip.listPassenger
          ?.split(', ')
          .map(id => {
            const found = selectDataUsers.find(item => item.label === id);

            return found ? found : { value: Number(id), label: `ID: ${id}` };
          });

        setPreSelectedUsers(selectedValueObjects);
      }

      setCustomTimes(actualHours);
      setTimePreset('custom');
      setSelectedDay(dayjs(updatingTrip.originalStart).toDate());
      if (updatingTrip.outsideUsers) {
        setTempOutsidePassengersAmount(`${updatingTrip.outsideUsers}`);
      }

      setFormRequestData({
        ...requestData,
        start: dayjs(updatingTrip.originalStart).format('YYYY-MM-DD HH:mm:ss'),
        end: dayjs(updatingTrip.originalEnd).format('YYYY-MM-DD HH:mm:ss'),
        takecar: updatingTrip.takeCar,
        leavecar: updatingTrip.leaveCar,
        canChangeTime: updatingTrip.canChangeTime,
        location: updatingTrip.companiesList?.split(', ').map(n => Number(n)),
        outsideUsers: updatingTrip.outsideUsers,
        users: updatingTrip.listPassenger
          ?.split(', ')
          .flatMap(id =>
            selectDataUsers
              .filter(item => item.label === id)
              .map(item => item.value)
          )
      });
    }
  }, [openNewEventModal, updatingTrip]);

  // join both ??
  useEffect(() => {
    resetRequest();
    resetLocation();
  }, []);

  useEffect(() => {
    getListOfCompanies();
    getListOfUsers();
  }, []);

  useEffect(() => {
    if (
      openNewEventModal &&
      newEventModalMode === 'create' &&
      currentUser &&
      preSelectedUsers.length === 0
    ) {
      const mainUser = selectDataUsers.find(
        user => user.value === currentUser.id_user
      );

      if (mainUser) {
        setPreSelectedUsers([mainUser]);
        setFormRequestData(prev => ({
          ...prev,
          users: [mainUser.value]
        }));
      }
    }
  }, [
    currentUser,
    newEventModalMode,
    openNewEventModal,
    preSelectedUsers.length,
    selectDataUsers
  ]);

  apiClient.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      if (error.response?.status === 401) {
        // Token expired or invalid
        // console.log('401 Interceptor response:', error);
      }
      if (error.response?.status === 404) {
        // Token expired or invalid
        // console.log('404 Interceptor response:', error);
      }
      if (error.response?.status === 409) {
        // Token expired or invalid
        const groupOverlaps = (data: UserConflict[]) => {
          const grouped: { [key: string]: string[] } = {};
          data.forEach(item => {
            if (!grouped[item.name]) {
              grouped[item.name] = [];
            }
            grouped[item.name].push(
              `de ${dayjs(item.start).format('D [de] MMM [as] HH[h]mm')} a ${dayjs(item.end).format('D [de] MMM [as] HH[h]mm')}`
            );
          });
          return grouped;
        };

        const processedOverlaps = groupOverlaps(
          error.response.data.conflictingTripId
        );

        const triggerOverlapToast = (
          message: string,
          detailsElement: React.ReactNode
        ) => {
          setToastMessage(message);
          setToastDetails(detailsElement);
          setShowToast(true);
          setIsLoading(false);
        };

        const OverlapList: FC<OverlapListProps> = ({ groupedData }) => (
          <ul className="list-unstyled mb-0 ms-3">
            {Object.entries(groupedData).map(([user, times], index) => (
              <li key={index} className="mt-2">
                {/* Highlight the user and count the trips */}
                <p className="fw-bold mb-1 text-decoration-underline">
                  {user} ({times.length}{' '}
                  {times.length > 1 ? 'sobreposições' : 'sobreposição'}):
                </p>
                {/* List the conflicting times below the user */}
                <ul className="mb-0 list-unstyled">
                  {times.map((time, timeIndex) => (
                    <li key={timeIndex}>• {time}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        );

        // Pop up on the side
        triggerOverlapToast(
          'Erro de Reserva: Conflito de Data',
          <OverlapList groupedData={processedOverlaps} /> // Pass the component here
        );
      }

      return Promise.reject(error);
    }
  );

  if (openAlertModal) {
    return (
      <Modal
        show={openAlertModal}
        contentClassName="border-translucent"
        onHide={() => {
          setOpenAlertModal(false);
          setErrorMessage('');
        }}
      >
        <Modal.Header className="px-card border-0 pb-0">
          <div className="w-100 d-flex justify-content-between align-items-start">
            <p>Alerta</p>
            <Button
              className="p-1 fs-10 text-body"
              onClick={() => {
                setOpenAlertModal(false);
                setErrorMessage('');
              }}
            >
              X
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="p-card py-0">
          <div>
            <p className="px-card border-0">{errorMessage}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-none border-0 p-0"></Modal.Footer>
      </Modal>
    );
  }

  if (createLocation) {
    return (
      <Modal
        show={openNewEventModal}
        contentClassName="border-translucent"
        onHide={stopLocationCreation}
      >
        <Form onSubmit={handleLocationCreation}>
          <Modal.Header className="px-card border-0">
            <div className="w-100 d-flex justify-content-between align-items-start">
              <div>
                <h5 className="mb-0 lh-sm text-body-highlight">
                  Adicionar um novo local
                </h5>
              </div>
              <Button
                className="p-1 fs-10 text-body"
                onClick={stopLocationCreation}
              >
                X
              </Button>
            </div>
          </Modal.Header>
          <Modal.Body>
            <FloatingLabel label="Nome curto" className="mb-3">
              <Form.Control
                required
                type="text"
                value={locationData.short}
                onChange={e => {
                  setFormLocationData({
                    ...locationData,
                    short: e.target.value
                  });
                }}
                isInvalid={!locationShortValidity}
              />
            </FloatingLabel>
            <FloatingLabel label="Nome longo" className="mb-3">
              <Form.Control
                required
                type="text"
                value={locationData.long}
                onChange={e => {
                  setFormLocationData({
                    ...locationData,
                    long: e.target.value
                  });
                }}
                isInvalid={!locationLongValidity}
              />
            </FloatingLabel>
            <FloatingLabel label="Nome da rua" className="mb-3">
              <Form.Control
                required
                type="text"
                value={locationData.road}
                onChange={e => {
                  setFormLocationData({
                    ...locationData,
                    road: e.target.value
                  });
                }}
                isInvalid={!locationRoadValidity}
              />
            </FloatingLabel>
            <FloatingLabel
              label="Código postal"
              className={locationPostalCodeValidity ? `mb-3` : ``}
            >
              <Form.Control
                required
                type="text"
                value={locationData.postalCode}
                onChange={e => {
                  setLocationPostalCodeValidity(true);
                  const raw = e.target.value.replace(/\D/g, '').slice(0, 7);
                  const postalCode =
                    raw.length > 4 ? `${raw.slice(0, 4)}-${raw.slice(4)}` : raw;
                  setFormLocationData({
                    ...locationData,
                    postalCode
                  });
                }}
                isInvalid={!locationPostalCodeValidity}
              />
            </FloatingLabel>
            {!locationPostalCodeValidity && (
              <div
                className={!locationPostalCodeValidity ? `mb-3` : ``}
                style={{
                  color: 'red',
                  fontSize: 12,
                  marginTop: 0,
                  marginLeft: 2
                }}
              >
                Formato inválido. Utilize o formato 1234-567.
              </div>
            )}
            <FloatingLabel label="NIF" className="mb-3">
              <Form.Control
                required
                type="text"
                value={locationData.nif}
                onChange={e => {
                  setLocationNifValidity(true);
                  setFormLocationData({
                    ...locationData,
                    nif: e.target.value
                  });
                }}
                isInvalid={!locationNifValidity}
              />
            </FloatingLabel>
            <FloatingLabel label="Cidade" className="mb-3">
              <Form.Control
                required
                type="text"
                value={locationData.city}
                onChange={e => {
                  setFormLocationData({
                    ...locationData,
                    city: e.target.value
                  });
                }}
                isInvalid={!locationCityValidity}
              />
            </FloatingLabel>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end align-items-center border-0">
            <Button variant="primary" type="submit" className="px-4">
              {isLoading ? (
                <Spinner
                  animation="border"
                  role="status"
                  variant="light"
                  size="sm"
                />
              ) : (
                'Guardar'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }

  if (deleteModalOpen) {
    return (
      <Modal
        show={deleteModalOpen}
        contentClassName="border-translucent"
        onHide={() => setDeleteModalOpen(false)}
      >
        <Modal.Header className="px-card border-0">
          <div className="w-100 d-flex justify-content-between align-items-start">
            <h5 className="mb-0 lh-sm text-body-highlight">
              Tem a certeza que quer apagar o pedido ?
            </h5>
            <Button
              className="p-1 fs-10 text-body"
              onClick={() => setDeleteModalOpen(false)}
            >
              ANULAR
            </Button>
          </div>
        </Modal.Header>
        <Modal.Footer className="d-flex w-100 justify-content-center align-items-center border-0">
          <Button
            variant="danger"
            type="button"
            onClick={() => deleteRequest('single')}
            className="flex-fill position-relative px-4"
          >
            <span className={isDeletingSingleRequest ? 'invisible' : undefined}>
              Apagar o pedido
            </span>
            {isDeletingSingleRequest && (
              <Spinner
                animation="border"
                role="status"
                variant="light"
                size="sm"
                aria-label="A carregar"
                className="position-absolute top-0 bottom-0 start-0 end-0 m-auto"
              />
            )}
          </Button>
          {updatingTrip?.id == updatingTrip?.grupo && (
            <Button
              variant="danger"
              type="button"
              onClick={() => deleteRequest('all')}
              className="flex-fill position-relative px-4"
            >
              <span className={isDeletingAllRequests ? 'invisible' : undefined}>
                Apagar os pedidos do grupo
              </span>
              {isDeletingAllRequests && (
                <Spinner
                  animation="border"
                  role="status"
                  variant="light"
                  size="sm"
                  aria-label="A carregar"
                  className="position-absolute top-0 bottom-0 start-0 end-0 m-auto"
                />
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      show={openNewEventModal}
      contentClassName="creation-trip-modal"
      centered
      onHide={handleClose}
    >
      <Form onSubmit={handleSubmit}>
        <Modal.Header className="px-card border-0">
          <div className="w-100 d-flex justify-content-between align-items-start">
            <div>
              <h5 className="mb-0 lh-sm text-body-highlight">
                {newEventModalMode == 'create'
                  ? 'Novo pedido'
                  : 'Modificar o pedido'}
              </h5>
            </div>
            <Button className="p-1 fs-10 text-body" onClick={handleClose}>
              X
            </Button>
          </div>
        </Modal.Header>
        {/* form para pedidio de carro */}
        {/* {requestData.eventType === 'car' && ( */}
        <>
          <Modal.Body className="p-card py-0">
            {/* 1. Day Selection */}
            <DatePicker
              value={selectedDay ?? undefined}
              hideIcon
              options={{
                enableTime: false,
                minDate: 'today',
                dateFormat: 'Y-m-d'
              }}
              onChange={([date]) => {
                setSelectedDay(date ?? null);
                setPastDate(true);
                setStartValidity(true);
                setEndValidity(true);
                updateEventTimes(date, timePreset, customTimes);
              }}
              render={(_, ref) => (
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="dayPicker">Dia da Viagem</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Selecione uma data"
                      ref={ref}
                      id="dayPicker"
                      className={`ps-6 ${selectedDay ? '' : 'border-primary'}`}
                      disabled={newEventModalMode == 'edit'}
                      isInvalid={!pastDate}
                    />
                    <UilCalendarAlt
                      className="position-absolute top-50 start-0 translate-middle-y ms-3 text-body-tertiary pe-none"
                      size={16}
                    />
                  </div>
                  <Form.Control.Feedback type="invalid">
                    Selecione uma data.
                  </Form.Control.Feedback>
                  {!pastDate && (
                    <div
                      className={!locationValidity ? `mb-3` : ``}
                      style={{
                        color: 'red',
                        fontSize: 12,
                        marginTop: 4,
                        marginLeft: 2
                      }}
                    >
                      É necessário selecionar uma data.
                    </div>
                  )}
                </Form.Group>
              )}
            />
            {/* 2. Preset Selection */}
            <div className="mb-1">
              <label className="form-label d-block text-700 fw-bold mb-1">
                Horário
              </label>
              <div
                className="d-grid gap-2"
                style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}
              >
                {[
                  { id: 'morning', label: 'Manhã' },
                  { id: 'afternoon', label: 'Tarde' },
                  { id: 'allday', label: 'Dia Inteiro' }
                ].map(index => (
                  <Button
                    key={index.id}
                    variant="outline-primary"
                    size="sm"
                    disabled={
                      newEventModalMode == 'edit' &&
                      dayjs(updatingTrip?.originalStart).isBefore(dayjs())
                    }
                    onClick={() => {
                      const preset = index.id as TimePreset;
                      setTimePreset(preset);
                      const presetTimes = {
                        morning: { start: '09:00', end: '12:30' },
                        afternoon: { start: '14:00', end: '18:00' },
                        allday: { start: '09:00', end: '18:00' },
                        custom: { start: '09:00', end: '18:00' }
                      };
                      const newTimes = presetTimes[preset];
                      setCustomTimes(newTimes);
                      updateEventTimes(selectedDay, preset, newTimes);
                    }}
                  >
                    {index.label}
                  </Button>
                ))}
              </div>
            </div>
            {/* 3. Time Inputs (Always shown) */}
            <div
              className={`d-flex gap-2 ${!(!startValidity || !endValidity) ? `mb-3` : ``}`}
            >
              <Form.Group className="flex-fill">
                <Form.Label className="small mb-1">Hora de Início</Form.Label>
                <Form.Control
                  type="time"
                  value={customTimes.start}
                  isInvalid={!startValidity}
                  disabled={
                    newEventModalMode == 'edit' &&
                    dayjs(updatingTrip?.originalStart).isBefore(dayjs())
                  }
                  onChange={e => {
                    setStartValidity(true);
                    setEndValidity(true);
                    const newTimes = {
                      ...customTimes,
                      start: e.target.value
                    };
                    setCustomTimes(newTimes);
                    setTimePreset('custom');
                    updateEventTimes(selectedDay, 'custom', newTimes);
                  }}
                />
              </Form.Group>
              <Form.Group className="flex-fill">
                <Form.Label className="small mb-1">Hora de Fim</Form.Label>
                <Form.Control
                  type="time"
                  value={customTimes.end}
                  isInvalid={!endValidity}
                  // min={dayjs().format('HH:mm')}
                  onChange={e => {
                    setStartValidity(true);
                    setEndValidity(true);
                    const newTimes = { ...customTimes, end: e.target.value };
                    setCustomTimes(newTimes);
                    setTimePreset('custom');
                    updateEventTimes(selectedDay, 'custom', newTimes);
                  }}
                />
              </Form.Group>
            </div>
            {(!startValidity || !endValidity) && (
              <div
                className="mb-3"
                style={{
                  color: 'red',
                  fontSize: 12,
                  marginTop: 4,
                  marginLeft: 2
                }}
              >
                Verifique a data.
              </div>
            )}
            {/* End of update */}
            <Form.Label>Destinos</Form.Label>
            {/* CreatableSelect */}
            <CreatableSelect
              isMulti
              className={!locationValidity ? `` : `mb-3`}
              options={selectDataCompanies}
              filterOption={(option, input) =>
                matchesSelectOption(option.label, input)
              }
              value={preSelectedCompanies}
              placeholder="Adicionar destinos"
              name="location"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: !locationValidity
                    ? '#dc3545'
                    : provided.borderColor,
                  '&:hover': {
                    borderColor: !locationValidity
                      ? '#dc3545'
                      : provided.borderColor
                  },
                  boxShadow:
                    !locationValidity && state.isFocused
                      ? '0 0 0 1px #dc3545'
                      : provided.boxShadow
                })
              }}
              onCreateOption={data => {
                // Prefill short name and open creation modal
                setFormLocationData({
                  ...locationData,
                  short: data
                });
                setCreateLocation(true);
              }}
              onChange={data => {
                setPreSelectedCompanies([...data]);
                const locations = data.map(company => company.value);
                setLocationValidity(true);
                setFormRequestData(prev => ({
                  ...prev,
                  location: locations
                }));
              }}
            />
            {!locationValidity && (
              <div
                className={!locationValidity ? `mb-3` : ``}
                style={{
                  color: 'red',
                  fontSize: 12,
                  marginTop: 4,
                  marginLeft: 2
                }}
              >
                É necessário selecionar pelo menos um destino.
              </div>
            )}
            <Form.Label>Outros passageiros</Form.Label>
            <Select
              isMulti
              options={selectDataUsers}
              filterOption={(option, input) =>
                matchesSelectOption(option.label, input)
              }
              value={preSelectedUsers}
              placeholder="Adicionar passageiros"
              name="users"
              className="mb-3"
              // isDisabled={newEventModalMode == 'edit'}
              onChange={data => {
                setPreSelectedUsers([...data]);
                const usersData = data.map(user => user.value);
                setPassengerValidity(true);
                setFormRequestData(prev => ({
                  ...prev,
                  users: usersData
                }));
              }}
            />
            {!passengerValidity && (
              <div
                className="mb-3"
                style={{ color: 'red', fontSize: 12, marginTop: -4 }}
              >
                É necessário selecionar pelo menos um passageiro ou indicar
                passageiros externos.
              </div>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Externos</Form.Label>
              <Form.Control
                type="number"
                value={tempOutsidePassengersAmount}
                min={0}
                max={5}
                // onChange={e => {
                //   const amount = e.target.value;
                //   setOutsidePassengersAmount(
                //     amount === '' ? 0 : Number(e.target.value)
                //   );
                // }}
                onChange={e => setTempOutsidePassengersAmount(e.target.value)}
                onBlur={e => {
                  const parsed = Number(tempOutsidePassengersAmount);
                  if (tempOutsidePassengersAmount === '' || isNaN(parsed)) {
                    setTempOutsidePassengersAmount(String(e.target.value)); // revert to last valid value
                  } else {
                    const clamped = Math.max(0, Math.min(5, parsed));
                    setFormRequestData(prev => ({
                      ...prev,
                      outsideUsers: clamped
                    }));
                    setTempOutsidePassengersAmount(String(clamped));
                  }
                }}
              />
            </Form.Group>
            <div>
              <Form.Check type="checkbox" className="mb-0">
                <Form.Check.Input
                  type="checkbox"
                  name="takecar"
                  id="takecar"
                  onChange={e => {
                    setFormRequestData(prev => ({
                      ...prev,
                      takecar: e.target.checked ? 'Y' : 'N'
                    }));
                  }}
                  checked={requestData.takecar === 'Y'}
                />
                <Form.Check.Label htmlFor="takecar" className="mb-0">
                  Levantar o carro no dia anterior
                </Form.Check.Label>
              </Form.Check>
              <Form.Check type="checkbox" className="mb-0">
                <Form.Check.Input
                  type="checkbox"
                  name="leavecar"
                  id="leavecar"
                  onChange={e => {
                    setFormRequestData(prev => ({
                      ...prev,
                      leavecar: e.target.checked ? 'Y' : 'N'
                    }));
                  }}
                  checked={requestData.leavecar === 'Y'}
                />
                <Form.Check.Label htmlFor="leavecar" className="mb-0">
                  Deixar o carro no dia seguinte
                </Form.Check.Label>
              </Form.Check>
              <Form.Check type="checkbox" className="mb-0">
                <Form.Check.Input
                  type="checkbox"
                  name="canChangeTime"
                  id="canChangeTime"
                  onChange={e => {
                    setFormRequestData(prev => ({
                      ...prev,
                      canChangeTime: e.target.checked ? 'N' : 'Y'
                    }));
                  }}
                  checked={requestData.canChangeTime === 'N'}
                />
                <Form.Check.Label htmlFor="canChangeTime" className="mb-0">
                  Não posso aceitar alteração do horário
                </Form.Check.Label>
              </Form.Check>
            </div>
          </Modal.Body>
        </>
        {/* )} */}
        <Modal.Footer className="d-flex w-100 align-items-center border-0">
          {newEventModalMode == 'create' ? (
            <Button
              variant="primary"
              type="submit"
              className="flex-fill position-relative px-4"
            >
              <span className={isCreatingRequest ? 'invisible' : undefined}>
                Criar
              </span>
              {isCreatingRequest && (
                <Spinner
                  animation="border"
                  role="status"
                  variant="light"
                  size="sm"
                  aria-label="A carregar"
                  className="position-absolute top-0 bottom-0 start-0 end-0 m-auto"
                />
              )}
            </Button>
          ) : (
            <>
              {/* <Button
                variant="danger"
                type="button"
                onClick={() => setDeleteModalOpen(true)}
                className="px-4"
              >
                Apagar o meu pedido
              </Button> */}
              <Button
                variant="danger"
                type="button"
                onClick={() => deleteRequest('single')}
                className="flex-fill position-relative px-4"
              >
                <span
                  className={isDeletingSingleRequest ? 'invisible' : undefined}
                >
                  Apagar para mim
                </span>
                {isDeletingSingleRequest && (
                  <Spinner
                    animation="border"
                    role="status"
                    variant="light"
                    size="sm"
                    aria-label="A carregar"
                    className="position-absolute top-0 bottom-0 start-0 end-0 m-auto"
                  />
                )}
              </Button>
              {updatingTrip?.id == updatingTrip?.grupo && (
                <Button
                  variant="danger"
                  type="button"
                  onClick={() => deleteRequest('all')}
                  className="flex-fill position-relative px-4"
                >
                  <span
                    className={isDeletingAllRequests ? 'invisible' : undefined}
                  >
                    Apagar para todos
                  </span>
                  {isDeletingAllRequests && (
                    <Spinner
                      animation="border"
                      role="status"
                      variant="light"
                      size="sm"
                      aria-label="A carregar"
                      className="position-absolute top-0 bottom-0 start-0 end-0 m-auto"
                    />
                  )}
                </Button>
              )}
              <Button
                variant="primary"
                type="submit"
                className="flex-fill position-relative px-4"
              >
                <span className={isSavingRequest ? 'invisible' : undefined}>
                  Guardar
                </span>
                {isSavingRequest && (
                  <Spinner
                    animation="border"
                    role="status"
                    variant="light"
                    size="sm"
                    aria-label="A carregar"
                    className="position-absolute top-0 bottom-0 start-0 end-0 m-auto"
                  />
                )}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Form>
      {/* TOAST CONTAINER PLACEMENT */}
      <ToastContainer
        // position="middle-center" // Center the toast at the top of the modal
        className="p-3"
        style={{ zIndex: 10, position: 'fixed', left: 0, top: 0 }} // zIndex must be higher than modal content
      >
        <Toast show={showToast} onClose={() => setShowToast(false)}>
          <Toast.Header
            closeButton={false}
            className="d-flex justify-content-between align-items-center"
          >
            <strong className="me-auto">{toastMessage}</strong>
            <Button className="ms-2 p-0" onClick={() => setShowToast(false)}>
              <UilTimes className="fs-7 text-black" />
            </Button>
          </Toast.Header>
          <Toast.Body>{toastDetails}</Toast.Body>
        </Toast>
      </ToastContainer>
    </Modal>
  );
};

export default CalendarAddNewEventModal;
